import * as React from 'react'
import { AlertTriangle, Check, ChevronRight, Copy, Loader2, X } from 'lucide-react'
import {
  SIGNIN_FLOW_STEPS,
  SIGNIN_STEP_LABELS,
  type ApplicationResultDto,
  type SignInCallDto,
  type SignInFlowStep,
  type SignInResultDto,
  type SignInStreamEvent,
} from '@acs/shared'
import { cn } from '@/lib/cn'
import { streamSignIn } from '@/lib/signin-stream'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

type StepState = 'idle' | 'running' | 'done' | 'failed'

interface StepRow {
  step: SignInFlowStep
  state: StepState
  ms?: number
  attempts?: number
  message?: string
  /**
   * Local clock reading for when this step started running.
   *
   * Deliberately `Date.now()` here rather than the server's `at`: the two clocks
   * are not the same one, and a few seconds of skew would show a step that has
   * been running for "-3s" or that jumps on its first tick. The elapsed counter
   * is a UI affordance, so measuring it against the UI's own clock is both
   * correct and immune to skew.
   */
  startedAt?: number
}

function initialSteps(): StepRow[] {
  return SIGNIN_FLOW_STEPS.map((step) => ({ step, state: 'idle' }))
}

/**
 * The Start button's live view.
 *
 * Streams the sign-in and shows each step as it happens, then the captured
 * result. What it deliberately does NOT show is any token value: the server
 * sends presence booleans, so there is nothing secret to render or leak.
 */
export function SignInDialog({
  customerId,
  open,
  onOpenChange,
}: {
  customerId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [steps, setSteps] = React.useState<StepRow[]>(initialSteps)
  const [result, setResult] = React.useState<SignInResultDto | null>(null)
  const [calls, setCalls] = React.useState<SignInCallDto[]>([])
  const [running, setRunning] = React.useState(false)
  const [fatal, setFatal] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open || !customerId) return

    const controller = new AbortController()
    let cancelled = false
    setSteps(initialSteps())
    setResult(null)
    setCalls([])
    setFatal(null)
    setRunning(true)

    void (async () => {
      try {
        for await (const ev of streamSignIn(customerId, controller.signal)) {
          if (cancelled) return
          if (ev.type === 'call') setCalls((c) => [...c, ev.call])
          else apply(ev, setSteps, setResult, setFatal)
        }
      } catch (err) {
        if (!cancelled) setFatal(err instanceof Error ? err.message : 'Sign-in stream failed')
      } finally {
        if (!cancelled) setRunning(false)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [open, customerId])

  const title = running ? 'Signing in…' : result?.ok ? 'Signed in' : result || fatal ? 'Sign-in failed' : 'Start automation'

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={customerId ? `Customer ${customerId}` : undefined}
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            {running ? 'Run in background' : 'Close'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <ol className="space-y-1.5">
          {steps.map((row) => (
            <StepLine key={row.step} row={row} />
          ))}
        </ol>

        {fatal ? (
          <p className="flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
            <AlertTriangle aria-hidden="true" className="mt-px size-4 shrink-0" />
            <span>{fatal}</span>
          </p>
        ) : null}

        {result ? <ResultPanel result={result} /> : null}

        {calls.length > 0 ? <CallInspector calls={calls} /> : null}
      </div>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ *
 * Request/response inspector
 * ------------------------------------------------------------------ */

function CallInspector({ calls }: { calls: SignInCallDto[] }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium tracking-[0.06em] text-faint uppercase">
          API calls ({calls.length})
        </span>
        <span className="text-[11px] text-faint">secrets masked</span>
      </div>
      <div className="divide-y divide-line overflow-hidden rounded-md border border-line">
        {calls.map((call, i) => (
          <CallRow key={`${call.step}-${i}`} call={call} />
        ))}
      </div>
    </div>
  )
}

const STATUS_TONE = (s: number): string =>
  s >= 200 && s < 300 ? 'text-ok' : s >= 400 ? 'text-danger' : 'text-warn'

function CallRow({ call }: { call: SignInCallDto }) {
  const [open, setOpen] = React.useState(false)
  return (
    <div className="bg-s1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-s3"
      >
        <ChevronRight
          aria-hidden="true"
          className={cn('size-3.5 shrink-0 text-faint transition-transform', open && 'rotate-90')}
        />
        <span className="w-11 shrink-0 font-mono text-[11px] font-semibold text-muted">{call.method}</span>
        <span className={cn('w-9 shrink-0 font-mono text-[12px] font-semibold tabular', STATUS_TONE(call.status))}>
          {call.status}
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-fg">{pathOf(call.url)}</span>
        <span className="shrink-0 font-mono text-[11px] tabular text-faint">{call.ms}ms</span>
      </button>

      {open ? (
        <div className="space-y-2.5 border-t border-line bg-s2 px-3 py-2.5">
          <KV label="URL" value={call.url} />
          <Block title="Request headers" data={call.requestHeaders} />
          {call.requestBody !== null && call.requestBody !== undefined ? (
            <Block title="Request body" data={call.requestBody} />
          ) : null}
          <Block title="Response headers" data={call.responseHeaders} />
          <Block title="Response body" data={call.responseBody} />
        </div>
      ) : null}
    </div>
  )
}

function pathOf(url: string): string {
  try {
    const u = new URL(url)
    return u.pathname + u.search
  } catch {
    return url
  }
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[10.5px] font-medium tracking-[0.06em] text-faint uppercase">{label}</div>
      <div className="font-mono text-[11.5px] break-all text-fg">{value}</div>
    </div>
  )
}

function Block({ title, data }: { title: string; data: unknown }) {
  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2)
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10.5px] font-medium tracking-[0.06em] text-faint uppercase">{title}</span>
        <CopySmall value={text} />
      </div>
      <pre className="max-h-56 overflow-auto rounded-sm border border-line bg-s1 px-2 py-1.5 font-mono text-[11px] leading-relaxed break-all whitespace-pre-wrap text-fg">
        {text}
      </pre>
    </div>
  )
}

function CopySmall({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <button
      type="button"
      aria-label="Copy"
      className="grid size-5 place-items-center rounded-sm text-faint hover:bg-s3 hover:text-fg"
      onClick={() => {
        void navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      }}
    >
      {copied ? <Check className="size-3 text-ok" /> : <Copy className="size-3" />}
    </button>
  )
}

const STEP_ICON: Record<StepState, React.ReactNode> = {
  idle: <span className="size-2 rounded-full bg-neutral/50" />,
  running: <Loader2 aria-hidden="true" className="size-3.5 animate-spin text-accent" />,
  done: <Check aria-hidden="true" className="size-3.5 text-ok" />,
  failed: <X aria-hidden="true" className="size-3.5 text-danger" />,
}

/**
 * Milliseconds since `since`, ticking while `active`.
 *
 * The whole point of a streaming UI is that the operator can see it moving. A
 * step that shows nothing until it finishes reads as a hang for however long it
 * takes — and the OTP wait is routinely 30s — so a running step counts up in
 * real time and only freezes when the real duration arrives from the server.
 *
 * 100ms rather than every frame: fast enough that the digits look continuous,
 * slow enough to cost nothing, and it stops entirely when the step is not
 * running so an open dialog is not re-rendering forever.
 */
function useElapsed(since: number | undefined, active: boolean): number {
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    if (!active || since === undefined) return
    setNow(Date.now())
    const t = setInterval(() => setNow(Date.now()), 100)
    return () => clearInterval(t)
  }, [active, since])
  return since === undefined ? 0 : Math.max(0, now - since)
}

function StepLine({ row }: { row: StepRow }) {
  const label = SIGNIN_STEP_LABELS[row.step]
  const running = row.state === 'running'
  const elapsed = useElapsed(row.startedAt, running)

  return (
    <li
      className={cn(
        'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13.5px] transition-colors',
        running && 'bg-accent-soft',
        row.state === 'idle' && 'opacity-55'
      )}
    >
      <span className="grid w-4 place-items-center">{STEP_ICON[row.state]}</span>
      <span className={cn('flex-1', row.state === 'done' && 'text-fg', row.state === 'idle' && 'text-muted')}>
        {label}
      </span>
      {row.state === 'failed' && row.message ? (
        <span className="max-w-[55%] truncate text-[12px] text-danger" title={row.message}>
          {row.message}
        </span>
      ) : null}
      {running ? (
        // `tabular` keeps the digits from shuffling the row as they change —
        // a counter that jitters sideways reads as broken, not as live.
        <span className="font-mono text-[11.5px] tabular text-accent">{formatMs(elapsed)}</span>
      ) : null}
      {row.ms !== undefined && row.state === 'done' ? (
        <span className="font-mono text-[11.5px] tabular text-faint">
          {formatMs(row.ms)}
          {row.attempts && row.attempts > 1 ? ` ·${row.attempts}×` : ''}
        </span>
      ) : null}
    </li>
  )
}

/** `840ms` under a second, `2.4s` above — a five-digit ms count reads as noise. */
function formatMs(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
}

function ResultPanel({ result }: { result: SignInResultDto }) {
  if (!result.ok) {
    return (
      <div className="rounded-md border border-line bg-s2 p-3 text-[13px]">
        <p className="text-muted">
          Failed at <span className="font-medium text-fg">{result.failedStep}</span>
          {result.retryable ? ' · retryable' : ' · not retryable'} · {result.totalMs}ms
        </p>
      </div>
    )
  }

  const captured = Object.entries(result.captured).filter(([, v]) => v).length
  return (
    <div className="space-y-2.5 rounded-md border border-line bg-s2 p-3">
      <Field label="Candidate ID" value={result.candidateId ?? '—'} mono copy={result.candidateId ?? undefined} />
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
        <Meta label="Source" value={result.candidateSource ?? '—'} />
        <Meta label="Market" value={result.market || '—'} />
        <Meta label="Egress" value={result.egress ?? '—'} mono />
        <Meta label="Captured" value={`${captured}/6 tokens`} />
        <Meta label="Total" value={`${result.totalMs}ms`} mono />
        <Meta
          label="Expires"
          value={result.expiresAtMs ? new Date(result.expiresAtMs).toLocaleTimeString() : '—'}
        />
      </div>

      {result.application ? <ApplicationPanel application={result.application} /> : null}
    </div>
  )
}

/**
 * What the application step produced.
 *
 * Rendered only when the step actually ran and succeeded — a customer with no
 * schedule on file is skipped upstream, and showing an empty "Application"
 * heading for them would imply something failed when nothing did.
 *
 * `nextState` is given the most visual weight of the three, because it is the
 * one an operator acts on: it says what the funnel is waiting for.
 */
function ApplicationPanel({ application }: { application: ApplicationResultDto }) {
  return (
    <div className="space-y-2.5 border-t border-line pt-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium tracking-[0.06em] text-faint uppercase">
          Application
        </span>
        <span className="font-mono text-[11px] tabular text-faint">{application.ms}ms</span>
      </div>

      {/*
        No application is a real outcome, not a missing value.

        A Brazil-flow run arms the detectors and files nothing until a shift
        opens, so there is genuinely no id and no state to show. Rendering the
        empty fields would read as a create that half-worked; saying what
        happened instead is the honest panel.
      */}
      {application.applicationId === null ? (
        <p className="text-[13px] text-faint">
          No application filed yet — this run claims and creates in one call when the shift
          opens.
        </p>
      ) : (
        <Field
          label="Application ID"
          value={application.applicationId}
          mono
          copy={application.applicationId}
        />
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
        <Meta
          label="State"
          value={application.currentState ? humanState(application.currentState) : '—'}
        />
        <Meta label="Next" value={application.nextState ? humanState(application.nextState) : '—'} />
        <Meta label="Job" value={application.jobId} mono />
        <Meta label="Schedule" value={application.scheduleId} mono />
      </div>
    </div>
  )
}

/** `SHIFT_SELECTION_PENDING` → `Shift selection pending`. */
function humanState(s: string): string {
  const t = s.replace(/_/g, ' ').toLowerCase()
  return t.charAt(0).toUpperCase() + t.slice(1)
}

function Field({
  label,
  value,
  mono,
  copy,
}: {
  label: string
  value: string
  mono?: boolean
  copy?: string
}) {
  const [copied, setCopied] = React.useState(false)
  return (
    <div>
      <div className="mb-0.5 text-[11px] font-medium tracking-[0.06em] text-faint uppercase">{label}</div>
      <div className="flex items-center gap-2">
        <span className={cn('flex-1 break-all text-[13.5px] text-fg', mono && 'font-mono')}>{value}</span>
        {copy ? (
          <button
            type="button"
            aria-label={`Copy ${label}`}
            className="grid size-6 place-items-center rounded-sm text-faint hover:bg-s3 hover:text-fg"
            onClick={() => {
              void navigator.clipboard.writeText(copy)
              setCopied(true)
              setTimeout(() => setCopied(false), 1200)
            }}
          >
            {copied ? <Check className="size-3.5 text-ok" /> : <Copy className="size-3.5" />}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <span className="text-[11px] tracking-[0.05em] text-faint uppercase">{label}</span>
      <div className={cn('text-[13px] text-fg', mono && 'font-mono tabular')}>{value}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Event → state
 * ------------------------------------------------------------------ */

function apply(
  ev: SignInStreamEvent,
  setSteps: React.Dispatch<React.SetStateAction<StepRow[]>>,
  setResult: React.Dispatch<React.SetStateAction<SignInResultDto | null>>,
  setFatal: React.Dispatch<React.SetStateAction<string | null>>
): void {
  switch (ev.type) {
    case 'step_start':
      setSteps((s) =>
        s.map((r) => (r.step === ev.step ? { ...r, state: 'running', startedAt: Date.now() } : r))
      )
      break
    case 'step_done':
      setSteps((s) =>
        s.map((r) => (r.step === ev.step ? { ...r, state: 'done', ms: ev.ms, attempts: ev.attempts } : r))
      )
      break
    case 'step_fail':
      setSteps((s) =>
        s.map((r) => (r.step === ev.step ? { ...r, state: 'failed', ms: ev.ms, message: ev.message } : r))
      )
      break
    case 'result':
      setResult(ev.result)
      break
    case 'error':
      setFatal(ev.message)
      break
    case 'accepted':
      break
  }
}
